import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { form, FormField } from '@angular/forms/signals';
import { MatAutocomplete, MatAutocompleteOrigin, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatOption } from '@angular/material/core';
import { NavigationEnd, Router } from '@angular/router';
import { delay, Subject } from 'rxjs';
import { debounceTime, filter } from 'rxjs/operators';
import { RootSearchStore } from '../../../store';
import { ClickCursorDirective } from '../../directives/click-cursor.directive';
import { I18nPipe } from '../../i18n/i18n.pipe';
import { RtlDirective } from '../../i18n/rtl.directive';
import { HighlightPipe } from '../../pipes/highlight.pipe';
import { SafeHtmlPipe } from '../../pipes/safe-html.pipe';
import { DotsLoaderComponent } from '../dots-loader/dots-loader.component';

@Component({
  selector: 'gnx-analysis-search-bar',
  templateUrl: './analysis-search-bar.component.html',
  styleUrls: ['./analysis-search-bar.component.scss'],
  imports: [
    MatAutocompleteOrigin,
    RtlDirective,
    ClickCursorDirective,
    DotsLoaderComponent,
    MatAutocompleteTrigger,
    FormField,
    MatAutocomplete,
    MatOption,
    DatePipe,
    I18nPipe,
    SafeHtmlPipe,
    HighlightPipe,
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class AnalysisSearchBarComponent {
  private router = inject(Router);

  protected searchStore = inject(RootSearchStore);

  private destroyRef = inject(DestroyRef);
  private enterDisabled: boolean;

  private searchModel = signal({ search: '' });
  protected searchForm = form(this.searchModel);

  private userInput = new Subject<string>();

  constructor() {
    this.userInput
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(500))
      .subscribe((value) => this.searchStore.searchAnalyses({ payload: value }));

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
        delay(0),
      )
      .subscribe(() => {
        this.enterDisabled = false;
      });
  }

  protected get searchValue(): string {
    return this.searchForm.search().value();
  }

  onUserInput(value: string) {
    this.userInput.next(value);
  }

  onSelect(analysisId: number) {
    this.enterDisabled = true;
    this.router.navigate([`/analysis-tool/analysis/workbench/${analysisId}`]);
  }

  onSearch() {
    if (this.enterDisabled) {
      return;
    }
    if (this.searchValue) {
      this.router.navigate(['/analysis-tool/analyses'], { queryParams: { search: this.searchValue } });
    } else {
      this.onEscape();
    }
  }

  onEscape() {
    this.searchModel.set({ search: '' });
  }
}
